import type { ApolloError } from '@apollo/client';
import { gql, useQuery } from '@apollo/client';
import { useMemo } from 'react';

/*
 * Cloutik routers for a customer via the backend getRouters query (already filters by
 * customer_id, deriving it from each router's "#clientId - Name"). Used to inspect the
 * real data for the current event before wiring it into the Network tab (still mock).
 */

export interface CloutikRouterRaw {
  id: string;
  customerId?: number | null;
  ip?: string | null;
  name?: string | null;
  identity?: string | null;
  serial?: string | null;
  model?: string | null;
  firmware?: string | null;
  uptime?: string | null;
  last_con?: string | null;
  status?: string | null;
  timezone?: string | null;
}

const EVENT_ROUTERS = gql`
  query EventRouters($customerId: Float!) {
    getRouters(input: { customer_id: $customerId }) {
      success
      errors
      result {
        id
        customerId
        ip
        name
        identity
        serial
        model
        firmware
        uptime
        last_con
        status
        timezone
      }
    }
  }
`;

interface QueryResult {
  getRouters?: {
    success: boolean;
    errors?: string[] | null;
    result?: (CloutikRouterRaw | null)[] | null;
  } | null;
}

interface QueryVars {
  customerId: number;
}

export const useRouters = (
  customerId: number
): {
  routers: CloutikRouterRaw[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
} => {
  const { data, loading, error, refetch } = useQuery<QueryResult, QueryVars>(EVENT_ROUTERS, {
    variables: { customerId },
    skip: Number.isNaN(customerId),
    fetchPolicy: 'cache-and-network'
  });

  const routers = useMemo(
    () => (data?.getRouters?.result ?? []).filter((r): r is CloutikRouterRaw => Boolean(r)),
    [data]
  );

  return {
    routers,
    loading,
    error,
    refetch: (): void => {
      void refetch();
    }
  };
};
